import { chains, ParsedCall } from "@gearbox-protocol/sdk";
import {
  CopyButton,
  ExpandablCall,
  ExternalButton,
} from "@gearbox-protocol/ui-kit";
import React from "react";
import { isAddress, isHex } from "viem";
import { useChainName } from "@/hooks/use-chain-list";

interface ProposalCallProps {
  index: number;
  call: ParsedCall;
}

function ProposalCallArg({
  chainId,
  arg,
  value,
}: {
  chainId: number;
  arg: string;
  value: string;
}) {
  const explorer = Object.values(chains).find(c => c.id === chainId)
    ?.blockExplorers?.default.url;

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="font-medium text-muted-foreground">{arg}:</span>
      <div className="flex items-center gap-1 font-mono text-sm">
        {value}
        <div className="flex items-center">
          {isHex(value) && (
            <CopyButton
              text={value}
              name={isAddress(value) ? "Address" : "Hex"}
            />
          )}
          {isAddress(value) && explorer && (
            <ExternalButton
              url={`${explorer}${
                explorer.endsWith("/") ? "" : "/"
              }address/${value}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function ProposalCall({ index, call }: ProposalCallProps) {
  const chainName = useChainName(call.chainId);

  return (
    <ExpandablCall
      index={index}
      header={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{call.label}:</span>
            <span className="break-all">{call.functionName}</span>
          </div>

          <span className="text-muted-foreground font-mono">
            {"["}
            {chainName}
            {"]"}
          </span>
        </div>
      }
    >
      {Object.entries(call.args).map(([arg, value], idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: <Allow index as key for indistinguishable rows>
        <React.Fragment key={idx}>
          <ProposalCallArg
            chainId={call.chainId}
            arg={arg}
            value={String(value)}
          />
        </React.Fragment>
      ))}
    </ExpandablCall>
  );
}
